import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';

import { usePreviousState } from '@atlaskit/editor-common/hooks';
import { nodeViewsMessages as messages } from '@atlaskit/editor-common/media';
import type {
	ContextIdentifierProvider,
	MediaProvider,
} from '@atlaskit/editor-common/provider-factory';
import {
	isNodeSelectedOrInRange,
	SelectedState,
	setNodeSelection,
} from '@atlaskit/editor-common/utils';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import EditorCloseIcon from '@atlaskit/icon/core/cross';
import type { Identifier } from '@atlaskit/media-client';
import { getMediaFeatureFlag } from '@atlaskit/media-common';
import type { MediaClientConfig } from '@atlaskit/media-core/auth';
import { Filmstrip } from '@atlaskit/media-filmstrip';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import { stateKey as mediaStateKey } from '../pm-plugins/plugin-key';
import type { MediaOptions } from '../types';

import { createMediaNodeUpdater, type MediaNodeUpdater } from './mediaNodeUpdater';

const getIdentifier = (item: PMNode): Identifier => {
	if (item.attrs.type === 'external') {
		return {
			mediaItemType: 'external-image',
			dataURI: item.attrs.url,
		};
	}
	return {
		id: item.attrs.id,
		mediaItemType: 'file',
		collectionName: item.attrs.collection,
	};
};

const isNodeSelected =
	(props: { anchorPos: number; headPos: number; nodeSize: number }) =>
	(mediaItemPos: number, mediaGroupPos: number): boolean => {
		const selected = isNodeSelectedOrInRange(
			props.anchorPos,
			props.headPos,
			mediaGroupPos,
			props.nodeSize,
		);

		if (selected === SelectedState.selectedInRange) {
			return true;
		}
		if (selected === SelectedState.selectedInside && props.anchorPos === mediaItemPos) {
			return true;
		}
		return false;
	};

const prepareFilmstripItem =
	({
		allowLazyLoading,
		allowMediaInlineImages,
		enableDownloadButton,
		handleMediaNodeRemoval,
		getPos,
		intl,
		isMediaItemSelected,
		setMediaGroupNodeSelection,
		featureFlags,
	}: {
		allowLazyLoading: boolean | undefined;
		allowMediaInlineImages: boolean | undefined;
		enableDownloadButton: boolean | undefined;
		featureFlags: MediaOptions['featureFlags'] | undefined;
		getPos: () => number | undefined;
		handleMediaNodeRemoval: (node: PMNode | undefined, getPos: () => number | undefined) => void;
		isMediaItemSelected: (mediaItemPos: number, mediaGroupPos: number) => boolean;
		setMediaGroupNodeSelection: (mediaItemPos: number) => void;
	} & WrappedComponentProps) =>
	(item: PMNode, idx: number) => {
		// We declared this to get a fresh position every time
		const getNodePos = () => {
			const pos = getPos();

			if (typeof pos !== 'number') {
				// That may seems weird, but the previous type wasn't match with the real ProseMirror code. And a lot of Media API was built expecting a number
				// Because the original code would return NaN on runtime
				// We are just make it explict now.
				// We may run a deep investagation on Media code to figure out a better fix. But, for now, we want to keep the current behavior.
				// TODO: ED-13910 - prosemirror-bump leftovers
				return NaN;
			}

			return pos + idx + 1;
		};

		// Media Inline creates a floating toolbar with the same options, excludes these options if enabled
		const mediaInlineOptions = (allowMediaInline: boolean = false) => {
			if (!allowMediaInline) {
				return {
					shouldEnableDownloadButton: enableDownloadButton,
					actions: [
						{
							handler: handleMediaNodeRemoval.bind(null, undefined, getNodePos),
							icon: <EditorCloseIcon label={intl.formatMessage(messages.mediaGroupDeleteLabel)} />,
						},
					],
				};
			}
		};

		const mediaGroupPos = getPos();

		return {
			identifier: getIdentifier(item),
			isLazy: allowLazyLoading,
			selected: isMediaItemSelected(
				getNodePos(),
				typeof mediaGroupPos === 'number' ? mediaGroupPos : NaN,
			),
			onClick: () => {
				setMediaGroupNodeSelection(getNodePos());
			},
			...mediaInlineOptions(
				fg('platform_editor_remove_media_inline_feature_flag')
					? allowMediaInlineImages
					: getMediaFeatureFlag('mediaInline', featureFlags),
			),
		};
	};

/**
 * Keep returning the same ProseMirror Node, unless the node content changed.
 *
 * React uses shallow comparation with `Object.is`,
 * but that can cause multiple re-renders when the same node is given in a different instance.
 *
 * To avoid unnecessary re-renders, this hook uses the `Node.eq` from ProseMirror API to compare
 * previous and new values.
 */
const useLatestMediaGroupNode = (nextMediaNode: PMNode) => {
	const previousMediaNode = usePreviousState(nextMediaNode);
	const [mediaNode, setMediaNode] = React.useState(nextMediaNode);

	React.useEffect(() => {
		if (!previousMediaNode) {
			return;
		}

		if (!previousMediaNode.eq(nextMediaNode)) {
			setMediaNode(nextMediaNode);
		}
	}, [previousMediaNode, nextMediaNode]);

	return mediaNode;
};

const runMediaNodeUpdate = async ({
	mediaNodeUpdater,
	getPos,
	node,
	updateAttrs,
}: {
	getPos: () => number | undefined;
	mediaNodeUpdater: MediaNodeUpdater;
	node: PMNode;
	updateAttrs: boolean;
}) => {
	if (updateAttrs) {
		await mediaNodeUpdater.updateNodeAttrs(getPos);
	}

	const contextId = mediaNodeUpdater.getNodeContextId();
	if (!contextId) {
		await mediaNodeUpdater.updateNodeContextId(getPos);
	}

	const shouldNodeBeDeepCopied = await mediaNodeUpdater.shouldNodeBeDeepCopied();

	if (shouldNodeBeDeepCopied) {
		await mediaNodeUpdater.copyNodeFromPos(getPos, {
			traceId: node.attrs.__mediaTraceId,
		});
	}
};

const noop = () => {};

type MediaGroupProps = {
	allowLazyLoading?: boolean;
	// These two numbers have to be passed separately. They can technically be derived from the view, but
	// because the view is *reference* then `shouldComponentUpdate` can't identify changes from incoming props
	anchorPos: number; // This value is required so that shouldComponentUpdate can calculate correctly
	contextIdentifierProvider?: Promise<ContextIdentifierProvider>;
	disabled?: boolean;
	editorViewMode?: boolean;
	forwardRef?: (ref: HTMLElement) => void;
	getPos: () => number | undefined;
	headPos: number; // This value is required so that shouldComponentUpdate can calculate correctly
	isCopyPasteEnabled?: boolean;
	mediaOptions: MediaOptions;
	mediaProvider?: Promise<MediaProvider>;
	node: PMNode;
	view: EditorView;
} & WrappedComponentProps;

// eslint-disable-next-line @typescript-eslint/no-restricted-types
export const MediaGroupNext: React.FC<
	WithIntlProps<
		{
			allowLazyLoading?: boolean;
			// These two numbers have to be passed separately. They can technically be derived from the view, but
			// because the view is *reference* then `shouldComponentUpdate` can't identify changes from incoming props
			anchorPos: number; // This value is required so that shouldComponentUpdate can calculate correctly
			contextIdentifierProvider?: Promise<ContextIdentifierProvider>;
			disabled?: boolean;
			editorViewMode?: boolean;
			forwardRef?: (ref: HTMLElement) => void;
			getPos: () => number | undefined;
			headPos: number; // This value is required so that shouldComponentUpdate can calculate correctly
			isCopyPasteEnabled?: boolean;
			mediaOptions: MediaOptions;
			mediaProvider?: Promise<MediaProvider>;
			node: PMNode;
			view: EditorView;
		} & WrappedComponentProps
	>
> & {
	WrappedComponent: React.ComponentType<
		{
			allowLazyLoading?: boolean;
			// These two numbers have to be passed separately. They can technically be derived from the view, but
			// because the view is *reference* then `shouldComponentUpdate` can't identify changes from incoming props
			anchorPos: number; // This value is required so that shouldComponentUpdate can calculate correctly
			contextIdentifierProvider?: Promise<ContextIdentifierProvider>;
			disabled?: boolean;
			editorViewMode?: boolean;
			forwardRef?: (ref: HTMLElement) => void;
			getPos: () => number | undefined;
			headPos: number; // This value is required so that shouldComponentUpdate can calculate correctly
			isCopyPasteEnabled?: boolean;
			mediaOptions: MediaOptions;
			mediaProvider?: Promise<MediaProvider>;
			node: PMNode;
			view: EditorView;
		} & WrappedComponentProps
	>;
} = injectIntl(
	React.memo((props: MediaGroupProps) => {
		const {
			mediaOptions: {
				allowLazyLoading,
				allowMediaInlineImages,
				enableDownloadButton,
				featureFlags,
				fallbackMediaNameFetcher,
			},
			intl,
			getPos,
			anchorPos,
			headPos,
			view,
			disabled,
			editorViewMode,
			mediaProvider,
			contextIdentifierProvider,
			isCopyPasteEnabled,
		} = props;
		const mediaGroupNode = useLatestMediaGroupNode(props.node);
		const mediaPluginState = useMemo(() => {
			return mediaStateKey.getState(view.state);
		}, [view.state]);
		const mediaClientConfig = mediaPluginState?.mediaClientConfig;
		const handleMediaGroupUpdate = mediaPluginState?.handleMediaGroupUpdate;

		const [viewMediaClientConfig, setViewMediaClientConfig] = useState<
			MediaClientConfig | undefined
		>(undefined);
		const nodeSize = mediaGroupNode.nodeSize;

		const mediaNodesWithOffsets: Array<{ node: PMNode; offset: number }> = useMemo(() => {
			const result: Array<{ node: PMNode; offset: number }> = [];
			mediaGroupNode.forEach((item, childOffset) => {
				result.push({
					node: item,
					offset: childOffset,
				});
			});
			return result;
		}, [mediaGroupNode]);
		const previousMediaNodesWithOffsets = usePreviousState(mediaNodesWithOffsets);

		const handleMediaNodeRemoval = useMemo(() => {
			return disabled || !mediaPluginState ? noop : mediaPluginState.handleMediaNodeRemoval;
		}, [disabled, mediaPluginState]);

		const setMediaGroupNodeSelection = useCallback(
			(pos: number) => {
				setNodeSelection(view, pos);
			},
			[view],
		);
		const isMediaItemSelected = useMemo(() => {
			return isNodeSelected({
				anchorPos,
				headPos,
				nodeSize,
			});
		}, [anchorPos, headPos, nodeSize]);

		const filmstripItem = useMemo(() => {
			return prepareFilmstripItem({
				allowLazyLoading,
				allowMediaInlineImages,
				enableDownloadButton,
				handleMediaNodeRemoval,
				getPos,
				intl,
				isMediaItemSelected,
				setMediaGroupNodeSelection,
				featureFlags,
			});
		}, [
			allowLazyLoading,
			allowMediaInlineImages,
			enableDownloadButton,
			handleMediaNodeRemoval,
			getPos,
			intl,
			isMediaItemSelected,
			setMediaGroupNodeSelection,
			featureFlags,
		]);
		const items = useMemo(() => {
			return mediaNodesWithOffsets.map(({ node, offset }) => {
				return filmstripItem(node, offset);
			});
		}, [mediaNodesWithOffsets, filmstripItem]);

		useEffect(() => {
			setViewMediaClientConfig(mediaClientConfig);
		}, [mediaClientConfig]);

		useEffect(() => {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
			mediaNodesWithOffsets.forEach(({ node, offset }) => {
				const mediaNodeUpdater = createMediaNodeUpdater({
					view,
					mediaProvider,
					contextIdentifierProvider,
					node,
					isMediaSingle: false,
				});

				const updateAttrs = isCopyPasteEnabled || isCopyPasteEnabled === undefined;

				runMediaNodeUpdate({
					mediaNodeUpdater,
					node,
					updateAttrs,
					getPos: () => {
						const pos = getPos();

						if (typeof pos !== 'number') {
							return undefined;
						}

						return pos + offset + 1;
					},
				});
			});
		}, [
			view,
			contextIdentifierProvider,
			getPos,
			mediaProvider,
			mediaNodesWithOffsets,
			isCopyPasteEnabled,
		]);

		useEffect(() => {
			if (!handleMediaGroupUpdate || !previousMediaNodesWithOffsets) {
				return;
			}

			const old = previousMediaNodesWithOffsets.map(({ node }) => node);
			const next = mediaNodesWithOffsets.map(({ node }) => node);

			handleMediaGroupUpdate(old, next);

			return () => {
				handleMediaGroupUpdate(next, []);
			};
		}, [handleMediaGroupUpdate, mediaNodesWithOffsets, previousMediaNodesWithOffsets]);

		return (
			<Filmstrip
				items={items}
				mediaClientConfig={viewMediaClientConfig}
				featureFlags={featureFlags}
				shouldOpenMediaViewer={
					editorViewMode && editorExperiment('platform_editor_controls', 'control')
				}
				fallbackMediaNameFetcher={fallbackMediaNameFetcher}
			/>
		);
	}),
);

MediaGroupNext.displayName = 'MediaGroup';
